import {
  gql,
  useMutation,
  useSubscription,
  useQuery,
  useApolloClient,
} from '@apollo/client';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useAuthContext } from '@src/context/authContext';
import { dismissWithConcurrency } from '@utils/dismissWithConcurrency';

/**
 * Exported for unit testing. Marks each new incoming project as onboarded on
 * the backend (deduped via markedSet). When there are new IDs to mark,
 * refetches after all mutations settle to drive the unviewed list to empty —
 * which clears skipOnboardingRef in the caller. No refetch is issued when
 * all incoming IDs are already in markedSet (cache-and-network double-fire).
 */
export const processTaskInviteSkip = async ({
  incoming,
  markedSet,
  mutate,
  refetchUnviewed,
}: {
  incoming: { id: string }[];
  markedSet: Set<string>;
  mutate: (id: string) => Promise<unknown>;
  refetchUnviewed: () => Promise<unknown>;
}): Promise<void> => {
  const promises = incoming
    .filter((p) => !markedSet.has(p.id))
    .map((p) => {
      markedSet.add(p.id);
      const rollback = (err: unknown) => {
        // Remove from set so the next unviewedData update can retry —
        // without this, a transient failure would keep skipOnboardingRef
        // stuck true and suppress onboarding for unrelated future projects.
        markedSet.delete(p.id);
        // eslint-disable-next-line no-console
        console.error('Failed to mark project as onboarded', {
          projectId: p.id,
          err,
        });
      };
      return mutate(p.id)
        .then((result) => {
          // TA-10139: `mutate` here is a raw client.mutate() call (see
          // caller below) — with the client's global `errorPolicy: 'all'`,
          // GraphQL business errors resolve into `result.errors` instead of
          // rejecting, so `.catch()` alone never saw them. Check
          // `result.errors` too, or a genuine backend failure would
          // silently leave `p.id` marked as done with no retry.
          if ((result as { errors?: unknown[] })?.errors?.length) {
            rollback((result as { errors?: unknown[] }).errors);
          }
        })
        .catch(rollback);
    });
  if (promises.length > 0) {
    await Promise.allSettled(promises)
      .then(() => refetchUnviewed())
      .catch(() => {});
  }
};

/**
 * Exported for unit testing. Drops `projectId` from `dismissedIds` once its
 * markProjectOnboarded request has settled — either way — and reports the
 * failure if it failed.
 *
 * TA-10632: a project is suppressed for exactly as long as its request is in
 * flight, and no longer. Keeping the id after a success made the bookkeeping
 * self-sustaining: a suppressed project never enters the queue, so it never
 * renders a card, so it is never marked onboarded, so the server keeps listing
 * it as unviewed — which made the "server reports nothing unviewed" clear that
 * used to be the only way to empty this set unreachable, stranding every other
 * id in it for the rest of the session.
 *
 * TA-10139: with the client's global `errorPolicy: 'all'`, GraphQL business
 * errors resolve into `result.errors` instead of rejecting or reaching
 * `onError`, so both signals have to be checked (same guard as
 * `processTaskInviteSkip`).
 */
export const settleDismissal = ({
  projectId,
  dismissedIds,
  result,
  error,
}: {
  projectId: string;
  dismissedIds: Set<string>;
  result?: { errors?: readonly unknown[] | null } | null;
  error?: unknown;
}): void => {
  const failure = error ?? (result?.errors?.length ? result.errors : undefined);
  // The suppression has done its job either way: on success the server now
  // agrees the project is viewed, on failure the next unviewed list should
  // bring the card back.
  dismissedIds.delete(projectId);
  if (!failure) return;

  // Log the failure so it doesn't go unnoticed — the queue was already
  // updated optimistically.
  // eslint-disable-next-line no-console
  console.error('Failed to mark project as onboarded', {
    projectId,
    error: failure,
  });
};

/**
 * TA-10632: MAX_VISIBLE_ONBOARDING_CARDS bounds what renders, not the queue, so
 * "Dismiss All" covers the server's whole unviewed list — dozens of projects
 * after a bulk assignment. Re-exported from its util so existing importers (and
 * tests) keep their entry point; see `@utils/dismissWithConcurrency` for why it
 * lives there.
 */
export {
  dismissWithConcurrency,
  ONBOARDING_DISMISS_CONCURRENCY,
} from '@utils/dismissWithConcurrency';

export type OnboardingProject = {
  id: string;
  uuid: string;
  name: string;
  color?: string | null;
  icon?: string | null;
  initials?: string | null;
};

/**
 * Maximum number of onboarding cards rendered at once. Anything beyond this
 * stays queued and is surfaced as a "+N more" indicator instead of stacking
 * arbitrarily many full-height cards over the app.
 */
export const MAX_VISIBLE_ONBOARDING_CARDS = 3;

/**
 * Exported for unit testing. Splits the queue into the capped visible stack
 * and the count collapsed behind the "+N more" indicator.
 */
export const selectVisibleOnboarding = (
  queue: OnboardingProject[],
  max: number = MAX_VISIBLE_ONBOARDING_CARDS
): { visible: OnboardingProject[]; hiddenCount: number } => ({
  visible: queue.slice(0, max),
  hiddenCount: Math.max(queue.length - max, 0),
});

type ProjectOnboardingContextType = {
  currentProject: OnboardingProject | null;
  /** Capped stack of onboarding cards to render (see MAX_VISIBLE_ONBOARDING_CARDS). */
  visibleProjects: OnboardingProject[];
  /** Queued projects not rendered because of the cap — shown as "+N more". */
  hiddenCount: number;
  dismissProject: (projectId: string) => void;
  dismissAll: () => void;
  goToProject: (project: OnboardingProject) => void;
  pushProject: (project: OnboardingProject) => void;
};

const projectOnboardingContext = createContext<
  ProjectOnboardingContextType | undefined
>(undefined);

const MARK_PROJECT_ONBOARDED = gql`
  mutation markProjectOnboarded($id: ID!) {
    markProjectOnboarded(id: $id)
  }
`;

const GET_UNVIEWED_PROJECTS = gql`
  query getUnviewedProjectAssignments {
    getMe {
      id
      unviewedProjectAssignments {
        id
        uuid
        name
        color
        icon
        initials
      }
    }
  }
`;

// Separate subscription document so it doesn't conflict with the existing
// meUpdated subscription used elsewhere in the app.
const ME_UPDATED_ONBOARDING = gql`
  subscription meUpdatedOnboarding {
    meUpdated {
      unviewedProjectAssignments {
        id
        uuid
        name
        color
        icon
        initials
      }
    }
  }
`;

const mergeIncoming = (
  prev: OnboardingProject[],
  incoming: OnboardingProject[]
): OnboardingProject[] => {
  const existingIds = new Set(prev.map((p) => p.id));
  const newItems = incoming.filter((p) => !existingIds.has(p.id));
  return newItems.length > 0 ? [...prev, ...newItems] : prev;
};

export const ProjectOnboardingProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const client = useApolloClient();
  const { token } = useAuthContext();
  const [queue, setQueue] = useState<OnboardingProject[]>([]);

  // Capture the skip flag synchronously at mount so subsequent unviewedData
  // updates (cache-and-network refetch, subscription) don't race with it.
  const skipOnboardingRef = useRef(
    typeof window !== 'undefined' &&
      window.sessionStorage?.getItem('skip_project_onboarding') === '1'
  );
  // Projects whose markProjectOnboarded request is still in flight. The queue
  // is updated optimistically, so a refetch/subscription landing before the
  // mutation commits still lists them as unviewed — without this they would pop
  // back into the queue. Especially visible with "Dismiss All", which dismisses
  // several at once. Each id is dropped again as soon as its request settles
  // (see settleDismissal), so the set can never outlive the requests it covers.
  const dismissedIdsRef = useRef<Set<string>>(new Set());
  // Tracks which project IDs have already been marked during the skip window
  // so cache-and-network double-fires don't send duplicate mutations.
  const markedDuringSkipRef = useRef<Set<string>>(new Set());

  const dropDismissed = useCallback(
    (incoming: OnboardingProject[]) =>
      incoming.filter((p) => !dismissedIdsRef.current.has(p.id)),
    []
  );

  // Remove the sessionStorage entry immediately after capturing it so it
  // doesn't persist into unrelated components or future sessions.
  useEffect(() => {
    if (skipOnboardingRef.current) {
      window.sessionStorage?.removeItem('skip_project_onboarding');
    }
  }, []);

  const { data: unviewedData, refetch: refetchUnviewed } = useQuery(
    GET_UNVIEWED_PROJECTS,
    { fetchPolicy: 'cache-and-network', skip: !token }
  );

  // Seed / sync queue whenever the getMe cache entry updates.
  useEffect(() => {
    const incoming = unviewedData?.getMe?.unviewedProjectAssignments as
      | OnboardingProject[]
      | undefined;

    // If the query hasn't returned this field yet, don't touch the queue.
    if (!incoming) {
      return;
    }

    // If the server explicitly reports no unviewed assignments, clear the queue
    // so we don't show stale onboarding cards. Also clear the skip ref here so
    // it doesn't suppress onboarding for unrelated future project additions.
    if (incoming.length === 0) {
      setQueue([]);
      skipOnboardingRef.current = false;
      return;
    }

    // Users who signed up via a task invitation should not see the project
    // onboarding card — mark all unviewed projects as onboarded on the backend
    // and skip queuing. The ref is intentionally NOT cleared here: with
    // fetchPolicy 'cache-and-network' the effect can fire twice with the same
    // non-empty list (cache hit then network). We keep the ref enabled until the
    // server confirms empty (above), ensuring all cache/network firings are skipped.
    if (skipOnboardingRef.current) {
      void processTaskInviteSkip({
        incoming,
        markedSet: markedDuringSkipRef.current,
        mutate: (id) =>
          client.mutate({
            mutation: MARK_PROJECT_ONBOARDED,
            variables: { id },
          }),
        refetchUnviewed,
      });
      setQueue([]);
      return;
    }

    setQueue((prev) => mergeIncoming(prev, dropDismissed(incoming)));
    // `dropDismissed` and `refetchUnviewed` are listed so the effect can't close
    // over a stale one. Re-running on a new identity is harmless: mergeIncoming
    // returns `prev` untouched when there is nothing new, and
    // processTaskInviteSkip dedupes through markedDuringSkipRef.
  }, [unviewedData, client, dropDismissed, refetchUnviewed]);

  // Real-time: subscription fires on every meUpdated event.
  // Process subscription data immediately, then also refetch the query
  // as a fallback in case the subscription payload is incomplete.
  useSubscription(ME_UPDATED_ONBOARDING, {
    skip: !token,
    onData: ({ data }) => {
      const incoming = data?.data?.meUpdated?.unviewedProjectAssignments as
        | OnboardingProject[]
        | undefined;

      if (incoming?.length) {
        if (!skipOnboardingRef.current) {
          setQueue((prev) => mergeIncoming(prev, dropDismissed(incoming)));
        }
        client.refetchQueries({
          include: ['listProjects', 'listProjectsRecent'],
        });
      }

      // Always refetch to stay in sync, whether or not subscription
      // delivered the unviewedProjectAssignments payload.
      refetchUnviewed();
    },
  });

  const [markProjectOnboarded] = useMutation(MARK_PROJECT_ONBOARDED);

  // Resolves once the request has settled and `settleDismissal` has run —
  // never rejects, so dismissWithConcurrency can await it as a pacing signal.
  const markOnboarded = useCallback(
    (projectId: string): Promise<void> => {
      dismissedIdsRef.current.add(projectId);
      // No `onError` on purpose: supplying one makes Apollo resolve the promise
      // instead of rejecting it, while the client's global `errorPolicy: 'all'`
      // still leaves the error in `result.errors` — so one business error
      // reached both `onError` and `.then`, and every failed dismissal was
      // logged twice (and `.catch` was dead code). Without it the two error
      // shapes split cleanly: network errors reject into `.catch`, GraphQL
      // errors resolve into `result.errors`, which is exactly the split
      // `settleDismissal` is written and unit-tested for.
      return markProjectOnboarded({ variables: { id: projectId } })
        .then((result) =>
          settleDismissal({
            projectId,
            dismissedIds: dismissedIdsRef.current,
            result,
          })
        )
        .catch((error) =>
          settleDismissal({
            projectId,
            dismissedIds: dismissedIdsRef.current,
            error,
          })
        );
    },
    [markProjectOnboarded]
  );

  const shiftQueue = useCallback(
    (projectId: string) => {
      void markOnboarded(projectId);
      setQueue((prev) => prev.filter((p) => p.id !== projectId));
    },
    [markOnboarded]
  );

  const dismissProject = useCallback(
    (projectId: string) => {
      shiftQueue(projectId);
    },
    [shiftQueue]
  );

  // Dismisses every queued project, including the ones collapsed behind the
  // "+N more" indicator — each still needs its own markProjectOnboarded call so
  // the backend doesn't resurface them on the next load, paced so a bulk
  // assignment doesn't fire dozens of requests at once.
  const dismissAll = useCallback(() => {
    const projectIds = queue.map((p) => p.id);
    // Suppress the whole batch up front rather than letting each request add
    // its own id when its turn comes: with the fan-out capped the requests no
    // longer all start together, and a refetch landing mid-batch would
    // otherwise re-queue the projects still waiting for a slot.
    projectIds.forEach((id) => dismissedIdsRef.current.add(id));
    setQueue([]);
    void dismissWithConcurrency({ projectIds, dismiss: markOnboarded });
  }, [queue, markOnboarded]);

  const goToProject = useCallback(
    (project: OnboardingProject) => {
      shiftQueue(project.id);
    },
    [shiftQueue]
  );

  const pushProject = useCallback((project: OnboardingProject) => {
    setQueue((prev) => mergeIncoming(prev, [project]));
  }, []);

  const currentProject = queue[0] ?? null;
  // Memoized so the context value only changes when the queue itself does —
  // slice() would otherwise return a new array reference on every render.
  const { visible: visibleProjects, hiddenCount } = useMemo(
    () => selectVisibleOnboarding(queue),
    [queue]
  );

  const value = useMemo(
    () => ({
      currentProject,
      visibleProjects,
      hiddenCount,
      dismissProject,
      dismissAll,
      goToProject,
      pushProject,
    }),
    [
      currentProject,
      visibleProjects,
      hiddenCount,
      dismissProject,
      dismissAll,
      goToProject,
      pushProject,
    ]
  );

  return (
    <projectOnboardingContext.Provider value={value}>
      {children}
    </projectOnboardingContext.Provider>
  );
};

export const useProjectOnboarding = (): ProjectOnboardingContextType => {
  const ctx = useContext(projectOnboardingContext);
  if (!ctx) {
    throw new Error(
      'useProjectOnboarding must be used within ProjectOnboardingProvider'
    );
  }
  return ctx;
};
